import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Spinner } from "../components/ui";
import { accessNodeId } from "../config/access/catalog";
import { useAccess } from "../hooks/useAccess";

interface AccessProtectedRouteProps {
  basePath: string;
  appId?: string;
  appPrefix?: string;
  redirectTo?: string;
  children?: ReactNode;
}

export default function AccessProtectedRoute({
  basePath,
  appId,
  appPrefix,
  redirectTo = "/apps",
  children,
}: AccessProtectedRouteProps) {
  const {
    catalog,
    isEnforced,
    isLoading,
    canAccessAppOfId,
    canAccessSectionOfApp,
  } = useAccess();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const content = children ?? <Outlet />;

  if (!isEnforced) return <>{content}</>;

  const segments = location.pathname
    .slice(basePath.length)
    .split("/")
    .filter(Boolean);

  const resolvedAppId =
    appPrefix === undefined ? appId : buildPrefixedAppId(appPrefix, segments[0]);

  if (resolvedAppId === undefined) return <>{content}</>;

  const categories = catalog.appCategories[resolvedAppId] ?? [];

  if (categories.length === 0) return <>{content}</>;

  if (!canAccessAppOfId(resolvedAppId)) {
    return (
      <Navigate to={redirectTo} replace state={{ accessDenied: true }} />
    );
  }

  const sectionId = appPrefix === undefined ? segments[0] : segments[1];

  if (sectionId === undefined) return <>{content}</>;

  const isKnownSection = categories.some(
    (categoryId) =>
      catalog.nodes[accessNodeId(categoryId, resolvedAppId, sectionId)] !==
      undefined,
  );

  if (isKnownSection && !canAccessSectionOfApp(resolvedAppId, sectionId)) {
    return (
      <Navigate to={redirectTo} replace state={{ accessDenied: true }} />
    );
  }

  return <>{content}</>;
}

function buildPrefixedAppId(
  appPrefix: string,
  segment: string | undefined,
): string | undefined {
  return segment === undefined ? undefined : `${appPrefix}-${segment}`;
}
